
import { Component, Prop, Vue } from 'vue-property-decorator'
import { IMediaData, IPlatform } from '@/types/media-types'
import PlatformCrawlStatus from './components/platformCrawlStatus.vue'
import { MediaService, MediaTagService } from '@/services/index'

@Component({
    name: 'MediaTable',
    components: {
        PlatformCrawlStatus
    }
})
export default class extends Vue {
    @Prop({ required: true }) private mediaList!: IMediaData[]
    @Prop({ default: true }) private isMedia!: boolean
    @Prop({ required: false }) private callback!: (ordering: string) => void

    // 表格高度（用于固定表头）
    private tableHeight: number | string = 'auto'

    // 标签相关数据
    private tagDialogVisible = false
    private currentMedia: any = null
    private selectedTags: any[] = []
    private systemTags: any[] = []
    private inputVisible = false
    private inputValue = ''
    private saveTagsLoading = false
    private editingTag: any = null;
    private editingTagName: string = '';

    private mounted() {
        this.calcTableHeight()
        window.addEventListener('resize', this.calcTableHeight)
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.calcTableHeight)
    }

    private calcTableHeight() {
        this.$nextTick(() => {
            const el = this.$el as HTMLElement
            if (!el) return
            const rect = el.getBoundingClientRect()
            // 表格顶部到视口底部的可用高度，减去分页区和底部留白
            this.tableHeight = window.innerHeight - rect.top - 80
        })
    }

    private updatePlatformCallback(mediumId: number, platformId: number, status: string) {
        const media = this.mediaList.find((media: any) => media.id === mediumId)
        if (media) {
            const platform = media.platforms.find((platform: IPlatform) => platform.id === platformId)
            if (platform) {
                platform.binding_status = status
            }
        }
    }

    private mapPlatform(platforms: IPlatform[], name: string) {
        return platforms.find((platform: any) => platform.name === name) || { id: 0, accounts: [] }
    }

    private onEdit(data: any) {
        window.open('/media/' + data.id + '/edit')
    }

    private onGoNewsList(data: any) {
        window.open('/news/list?mediaId=' + data.id + '&name=' + data.name)
    }

    private onTapReporterCount(data: any) {
        window.open('/reporter/list?mediaId=' + data.id + '&name=' + data.name)
    }

    private onGoMediaPlatforms(data: any) {
        window.open('/media/' + data.id + '/platforms')
    }

    private onSortChange(props: any) {
        const prop = props.prop === 'region' ? 'province' : props.prop
        const ordering = props.order === 'ascending' ? prop : '-' + prop

        if (this.callback && ordering) {
            this.callback(ordering)
        }
    }

    private async updateMediaOrder(row: any, event: Event) {
        const target = event.target as HTMLElement
        const new_order = target.innerText.trim()


        const params = {
            id: row.id,
            name: row.name,
            type: row.type,
            ordering: new_order === '' ? null : new_order
        }

        const result = await MediaService.update(params)
        if (result.status === 200) {
            this.$message.success('权重更新成功')
        }
    }

    // 打开标签弹窗
    private async onTagMedia(media: any) {
        this.currentMedia = media
        this.tagDialogVisible = true
        await this.fetchMediaTags()
        await this.fetchAllTags()
    }

    // 获取媒体已选中的标签
    private async fetchMediaTags() {
        try {
            const result = await MediaTagService.getMediaTagRelation({ medium_id: this.currentMedia.id })
            if (result.status === 200 && result.data) {
                this.selectedTags = result.data
            } else {
                this.selectedTags = []
            }
        } catch (error) {
            this.selectedTags = []
            this.$message.error('获取已选标签失败')
        }
    }

    // 获取系统所有标签
    private async fetchAllTags() {
        try {
            const result = await MediaTagService.mediaTagSearch({ 'keyword': '' })
            if (result.status === 200 && result.data) {
                this.systemTags = result.data
            } else {
                this.systemTags = []
            }
        } catch (error) {
            this.systemTags = []
            this.$message.error('获取系统标签失败')
        }
    }

    // 移除已选中的标签
    private removeSelectedTag(tag: any) {
        this.selectedTags = this.selectedTags.filter(item => item.id !== tag.id)
    }

    // 检查标签是否已被选中
    private isTagSelected(tag: any) {
        return this.selectedTags.some(item => item.id === tag.id)
    }

    // 切换标签选中状态
    private toggleTagSelection(tag: any) {
        if (this.isTagSelected(tag)) {
            this.removeSelectedTag(tag)
        } else {
            this.selectedTags.push(tag)
        }
    }

    // 显示输入框
    private showInput() {
        this.inputVisible = true
        this.$nextTick(() => {
            (this.$refs.saveTagInput as any).$refs.input.focus()
        })
    }

    // 处理输入确认
    private async handleInputConfirm() {
        const inputValue = this.inputValue.trim()
        if (inputValue) {
            // 检查是否已存在相同名称的标签
            const existingTag = this.systemTags.find(tag => tag.name === inputValue)
            if (existingTag) {
                this.$message.warning('标签已存在')
                // 如果标签已存在但未选中，则选中它
                if (!this.isTagSelected(existingTag)) {
                    this.selectedTags.push(existingTag)
                }
            } else {
                try {
                    // 创建新标签
                    const result = await MediaTagService.createTag({ name: inputValue })
                    if (result.status === 200 && result.data) {
                        const newTag = result.data
                        this.systemTags.push(newTag)
                        this.selectedTags.push(newTag)
                        this.$emit('refresh-system-tags')
                    }
                } catch (error) {
                    this.$message.error('创建标签失败')
                }
            }
        }
        this.inputVisible = false
        this.inputValue = ''
    }

    // 保存标签
    private async saveTags() {
        if (!this.currentMedia) return

        this.saveTagsLoading = true
        try {
            const tagIds = this.selectedTags.map(tag => tag.id)
            const result = await MediaTagService.addMediaTagRelation({
                medium_id: this.currentMedia.id,
                tag_ids: tagIds
            })

            if (result.status === 200) {
                this.$message.success('标签保存成功')
                this.tagDialogVisible = false
            } else {
                this.$message.error('标签保存失败')
            }
        } catch (error) {
            this.$message.error('标签保存失败')
        } finally {
            this.saveTagsLoading = false
        }
    }

    // 删除系统标签
    private async deleteSystemTag(tag: any) {
        try {
            // 先确认是否要删除
            await this.$confirm(`确定要删除标签 "${tag.name}" 吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            })

            // 调用删除标签的API
            const result = await MediaTagService.deleteTag({ id: tag.id })
            if (result.status === 200) {
                // 从系统标签列表中移除
                this.systemTags = this.systemTags.filter(item => item.id !== tag.id)

                // 如果该标签在已选中列表中，也需要移除
                if (this.isTagSelected(tag)) {
                    this.removeSelectedTag(tag)
                }

                this.$message.success(result.message)
                this.$emit('refresh-system-tags')
            } else {
                this.$message({ type: 'error', message: result.message })
            }
        } catch (error) {
            // 用户取消删除或发生错误
            if (error !== 'cancel') {
                this.$message.error('删除标签失败')
            }
        }
    }

    // 开始编辑标签
    private startEditTag(tag: any) {
        this.editingTag = tag
        this.editingTagName = tag.name
    }

    // 保存编辑后的标签
    private async saveEditedTag() {
        if (!this.editingTag || this.editingTagName.trim() === '') return

        try {
            const result = await MediaTagService.updateTag({
                id: this.editingTag.id,
                name: this.editingTagName.trim()
            })

            if (result.status === 200) {
                const updatedTag = this.systemTags.find(tag => tag.id === this.editingTag.id)
                if (updatedTag) {
                    updatedTag.name = this.editingTagName.trim()
                }
                this.$message.success('标签更新成功')
                this.editingTag = null
                this.editingTagName = ''
                this.$emit('refresh-system-tags')
            } else {
                this.$message.error(result.message || '更新标签失败')
            }
        } catch (error) {
            this.$message.error('更新标签失败')
        }
    }

    // 取消编辑标签
    private cancelEditTag() {
        this.editingTag = null
        this.editingTagName = ''
    }

    // 关闭弹窗时清理数据
    private handleTagDialogClose() {
        this.currentMedia = null
        this.selectedTags = []
        this.systemTags = []
        this.inputVisible = false
        this.inputValue = ''
    }
}
